import { API_URL } from '../config';

// Vyčištění starých localStorage dat
if (typeof window !== 'undefined') {
  localStorage.removeItem('crm_company_statuses');
}

// Výchozí stavy pro společnosti
export const defaultStatuses = [
  "Nová",
  "Volat později", 
  "Nedovoláno",
  "Kontaktován",
  "Zájem",
  "Čekám na podklady",
  "Vytvořit nabídku",
  "Návrh zaslán",
  "Zákazník se rozhoduje",
  "V jednání",
  "Schůzka domluvena",
  "Vytvořit smlouvu",
  "Implementace",
  "Hotovo",
  "Nemá zájem",
  "Nedaří se spojit",
  "Odloženo"
];

// Funkce pro načtení stavů z backendu
export const getCompanyStatuses = async () => {
  try {
    const userId = typeof window !== 'undefined' ? localStorage.getItem('lastUserId') || '' : '';
    const response = await fetch(`${API_URL}/company-statuses?userId=${userId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const statuses = await response.json();
    
    console.log('Loaded statuses from backend:', statuses);
    
    // Pokud nejsou žádné stavy v databázi, vrať výchozí
    if (statuses.length === 0) {
      console.log('No statuses in database, using defaults');
      return defaultStatuses.map((status, index) => ({
        id: `default_${index}`, // Použij string ID pro defaultní stavy
        name: status,
        order: index,
        isDefault: true
      }));
    }
    
    // Ujisti se, že všechny stavy mají správné ID
    return statuses.map(status => ({
      ...status,
      // Zachovej MongoDB _id pokud existuje, jinak použij id
      _id: status._id || status.id,
      id: status._id || status.id
    }));
  } catch (error) {
    console.error('Chyba při načítání stavů z API:', error);
    
    // Výchozí stavy jako fallback
    console.log('Using default statuses as fallback');
    return defaultStatuses.map((status, index) => ({
      id: `fallback_${index}`,
      _id: `fallback_${index}`,
      name: status,
      order: index,
      isDefault: true
    }));
  }
};

// Funkce pro uložení stavů do backendu
export const saveCompanyStatuses = async (statuses) => {
  try {
    // Filtruj pouze stavy, které mají validní MongoDB ID (ne defaultní/lokální)
    const validStatuses = statuses
      .map(status => ({
        id: status._id || status.id,
        order: status.order
      }))
      .filter(status => {
        // Vyfiltruj stavy bez ID nebo s falešným ID
        const hasValidId = status.id && 
          typeof status.id === 'string' && 
          !status.id.startsWith('default_') && 
          !status.id.startsWith('local_') && 
          !status.id.startsWith('fallback_');
        
        if (!hasValidId) {
          console.log('Filtering out status with invalid ID:', status);
        }
        
        return hasValidId;
      });
    
    if (validStatuses.length === 0) {
      console.warn('Žádné platné stavy k uložení - všechny stavy jsou lokální/defaultní');
      throw new Error('Žádné platné stavy k uložení na server');
    }
    
    console.log('Odesílám data pro přeřazení:', validStatuses);
    
    const userId = typeof window !== 'undefined' ? localStorage.getItem('lastUserId') || '' : '';
    // Pro přeřazení stavů použijeme speciální endpoint
    const response = await fetch(`${API_URL}/company-statuses/reorder?userId=${userId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        statuses: validStatuses,
        userId
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.text();
      console.error('Backend error response:', errorData);
      throw new Error(`HTTP error! status: ${response.status}, response: ${errorData}`);
    }
    
    const result = await response.json();
    console.log('Successfully saved statuses to backend');
    return result;
  } catch (error) {
    console.error('Chyba při ukládání stavů do API:', error);
    throw error;
  }
};

// Funkce pro přidání nového stavu
export const addCompanyStatus = async (name, createdBy = null) => {
  try {
    const userId = typeof window !== 'undefined' ? localStorage.getItem('lastUserId') || '' : '';
    const response = await fetch(`${API_URL}/company-statuses?userId=${userId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: name.trim(),
        createdBy,
        userId
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Chyba při přidávání stavu:', error);
    throw error;
  }
};

// Funkce pro úpravu stavu
export const updateCompanyStatus = async (statusId, name = null, additionalData = {}) => {
  try {
    const updateData = { ...additionalData };
    
    // Přidej name pouze pokud je zadané
    if (name !== null) {
      updateData.name = name.trim();
    }
    
    const response = await fetch(`${API_URL}/company-statuses/${statusId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Chyba při aktualizaci stavu:', error);
    throw error;
  }
};

// Funkce pro smazání stavu
export const deleteCompanyStatus = async (statusId) => {
  try {
    const response = await fetch(`${API_URL}/company-statuses/${statusId}`, {
      method: 'DELETE',
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Chyba při mazání stavu:', error);
    throw error;
  }
};

// Funkce pro získání pouze názvů stavů (pro zpětnou kompatibilitu)
export const getCompanyStatusNames = async () => {
  try {
    const statuses = await getCompanyStatuses();
    return statuses.map(status => typeof status === 'string' ? status : status.name);
  } catch (error) {
    console.error('Chyba při získávání názvů stavů:', error);
    return defaultStatuses;
  }
};
